<script lang="ts" setup generic="T extends string | Component">
import {
  computed,
  useCssModule,
  useSlots,
  h,
  mergeProps,
  useAttrs,
  type Component,
  type VNodeChild,
} from 'vue';
import type { PolymorphicProps } from '../../shared/types';
const props = defineProps<
  PolymorphicProps<T> & {
    size?: 'm' | 's';
    variant?: 'success' | 'error' | 'warning';
    textColor?: string;
    backgroundColor?: string;
  }
>();
defineSlots<{
  default?: () => VNodeChild;
  subtitle?: () => VNodeChild;
  leadingVisual?: () => VNodeChild;
  trailingVisual?: () => VNodeChild;
}>();
const style = useCssModule();
const rootProps = computed(() => {
  return {
    class: [
      style.root,
      {
        [style.sizeS]: props.size === 's',
        [style.variantSuccess]: props.variant === 'success',
        [style.variantError]: props.variant === 'error',
        [style.variantWarning]: props.variant === 'warning',
      },
    ],
    style: {
      backgroundColor: props.backgroundColor,
      color: props.textColor,
    },
    'data-ui-component': 'UiTag',
  };
});
const slots = useSlots();
const attrs = useAttrs();
defineRender(() => {
  const title = slots.default?.();
  const subtitle = slots.subtitle?.();
  const leadingVisual = slots.leadingVisual?.();
  const trailingVisual = slots.trailingVisual?.();
  const internalStructure = [
    leadingVisual ? h('span', { class: style.leadingVisual }, leadingVisual) : null,
    h('span', { class: style.textContainer }, [
      title ? h('span', { class: style.title }, title) : null,
      subtitle ? h('span', { class: style.subtitle }, subtitle) : null,
    ]),
    trailingVisual ? h('span', { class: style.trailingVisual }, trailingVisual) : null,
  ];
  const computedProps = mergeProps(
    attrs,
    rootProps.value,
    props.as && props.asProps ? props.asProps : {},
  );
  if (props.as) {
    return h(props.as, computedProps, internalStructure);
  }
  return h('span', computedProps, internalStructure);
});
</script>

<style lang="scss" module>
@use '../../styles/typography';

.root {
  position: relative;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  height: 24px;
  border-radius: 4px;
  flex: none;
  background: #00000014;
  color: #010000;
  padding: 0 6px;
  --gap: 4px;
  max-width: 192px;
  @include typography.style('data-xs-medium');
}

.textContainer {
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.title,
.subtitle {
  display: inline-block;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
}

.title {
  min-width: 0;
  flex: 0 1 auto;
}

.subtitle {
  min-width: 0;
  flex: 0 999 auto;
}

.title + .subtitle {
  margin-left: var(--gap);
}

.leadingVisual,
.trailingVisual {
  display: inline-flex;
  width: 16px;
  height: 16px;
  vertical-align: middle;
  flex: none;

  > img,
  > svg {
    width: 100%;
    height: 100%;
  }
}

.leadingVisual:not(:last-child) {
  margin-right: var(--gap);
}

.trailingVisual:not(:first-child) {
  margin-left: var(--gap);
}

.sizeS {
  height: 20px;
  padding: 0 4px;
  --gap: 2px;

  .leadingVisual,
  .trailingVisual {
    width: 14px;
    height: 14px;
  }
}

.variantSuccess {
  background: rgba(24, 165, 130, 0.12);
  color: #00906e;
}

.variantError {
  background: rgba(230, 57, 70, 0.12);
  color: #b2001b;
}

.variantWarning {
  background: rgba(228, 161, 75, 0.16);
  color: #ac6800;
}
</style>
